/**
 * Mehrsprachigkeit im Mieterportal (MP-E): Deutsch ist die Quelle, Englisch, Ukrainisch und Türkisch für die meistgenutzten
 * Screens (Navigation, Start, Erfassen, Profil, Sicherheit). Nicht übersetzte Texte bleiben Deutsch. Fachbegriffe wie
 * „Regal“ oder „Etikett“ sind übersetzt, Markennamen nicht.
 */
import { useCockpit } from "./mieter/cockpit-api";
import { uebersetzeText } from "@shared/i18n-texte";

export type Sprache = "de" | "en" | "uk" | "tr";
export const SPRACHEN: { code: Sprache; name: string }[] = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
  { code: "uk", name: "Українська" },
  { code: "tr", name: "Türkçe" },
];

const DE = {
  zurueck: "Zurück",
  "nav.start": "Start",
  "nav.artikel": "Artikel",
  "nav.erfassen": "Erfassen",
  "nav.abrechnung": "Abrechnung",
  "nav.regal": "Regal",
  "nav.profil": "Profil",
  "nav.portal": "Mieterportal",
  "start.guthaben": "Dein Guthaben",
  "start.heuteNichts": "Heute noch nichts verkauft",
  "start.heute": "Heute",
  "start.losGehts": "Los geht’s",
  "start.fuerDich": "Für dich",
  "start.letzteVerkaeufe": "Letzte Verkäufe",
  "start.live": "Live",
  "start.nochNichts": "Noch nichts verkauft. Sobald etwas über die Kasse geht, steht es hier – sofort.",
  "start.zahlen": "Deine Zahlen · 30 Tage",
  "start.umsatz": "Umsatz",
  "start.verkauft": "Verkauft",
  "start.preis": "Ø Preis",
  "start.imRegal": "Ø im Regal",
  "start.regalCheck": "Regal-Check",
  "start.app": "Als App aufs Handy",
  "start.verkauftRuf": "Verkauft!",
  "kachel.erfassen": "Artikel erfassen",
  "kachel.erfassenSub": "Kamera zuerst, dann Preis",
  "kachel.etiketten": "Etiketten drucken",
  "kachel.packliste": "Einräum-Liste",
  "kachel.regal": "Mein Regal",
  "kachel.lagerhueter": "Liegt lange",
  "kachel.abrechnung": "Abrechnung",
  "kachel.teilen": "Teilen",
  "kachel.statistik": "Deine Zahlen",
  "kachel.aktionen": "Aktionen",
  "kachel.inventur": "Inventur",
  "kachel.termin": "Termin",
  "kachel.sammel": "Viele auf einmal",
  "erfassen.schritt1": "Foto machen",
  "erfassen.schritt2": "Was ist es, was kostet es?",
  "erfassen.schritt3": "Angelegt",
  "erfassen.foto": "Antippen und fotografieren",
  "erfassen.fotoHinweis": "Bei Tageslicht, Artikel füllt das Bild",
  "erfassen.galerie": "Aus der Galerie",
  "erfassen.ohneFoto": "Ohne Foto weiter",
  "erfassen.bezeichnung": "Bezeichnung",
  "erfassen.preis": "Preis",
  "erfassen.kategorie": "Kategorie",
  "erfassen.anlegen": "Anlegen",
  "erfassen.naechster": "Nächster Artikel",
  "erfassen.offline": "Kein Netz – der Artikel ist gespeichert und wird gesendet, sobald du wieder online bist.",
  "offline.warten": "warten auf Verbindung",
  "offline.senden": "Jetzt senden",
  "profil.titel": "Profil",
  "profil.kontakt": "Kontakt",
  "profil.auszahlung": "Auszahlung",
  "profil.speichern": "Speichern",
  "profil.sprache": "Sprache",
  "profil.anzeige": "Anzeige",
  "profil.grosseSchrift": "Große Schrift",
  "profil.einfach": "Einfach-Modus (nur das Wichtigste auf Start)",
  "profil.kacheln": "Startseite anpassen",
  "profil.abmelden": "Abmelden",
  "profil.sicherheit": "Sicherheit und Zugang",
  "profil.werben": "Freunde werben",
  "sicherheit.titel": "Sicherheit und Zugang",
  "sicherheit.geraete": "Angemeldete Geräte",
  "sicherheit.loeschen": "Konto löschen",
} as const;
export type Schluessel = keyof typeof DE;

const EN: Record<Schluessel, string> = {
  zurueck: "Back",
  "nav.start": "Home",
  "nav.artikel": "Items",
  "nav.erfassen": "Add",
  "nav.abrechnung": "Statements",
  "nav.regal": "Shelf",
  "nav.profil": "Profile",
  "nav.portal": "Seller portal",
  "start.guthaben": "Your balance",
  "start.heuteNichts": "Nothing sold yet today",
  "start.heute": "Today",
  "start.losGehts": "Let’s go",
  "start.fuerDich": "For you",
  "start.letzteVerkaeufe": "Recent sales",
  "start.live": "Live",
  "start.nochNichts": "Nothing sold yet. As soon as something goes through the till, it shows up here – instantly.",
  "start.zahlen": "Your numbers · 30 days",
  "start.umsatz": "Revenue",
  "start.verkauft": "Sold",
  "start.preis": "Avg. price",
  "start.imRegal": "Avg. days on shelf",
  "start.regalCheck": "Shelf check",
  "start.app": "Add to your phone",
  "start.verkauftRuf": "Sold!",
  "kachel.erfassen": "Add item",
  "kachel.erfassenSub": "Photo first, then price",
  "kachel.etiketten": "Print labels",
  "kachel.packliste": "Packing list",
  "kachel.regal": "My shelf",
  "kachel.lagerhueter": "Slow sellers",
  "kachel.abrechnung": "Statements",
  "kachel.teilen": "Share",
  "kachel.statistik": "Your numbers",
  "kachel.aktionen": "Promotions",
  "kachel.inventur": "Stock check",
  "kachel.termin": "Appointment",
  "kachel.sammel": "Bulk upload",
  "erfassen.schritt1": "Take a photo",
  "erfassen.schritt2": "What is it, what does it cost?",
  "erfassen.schritt3": "Added",
  "erfassen.foto": "Tap to take a photo",
  "erfassen.fotoHinweis": "Daylight, item fills the frame",
  "erfassen.galerie": "From gallery",
  "erfassen.ohneFoto": "Continue without photo",
  "erfassen.bezeichnung": "Description",
  "erfassen.preis": "Price",
  "erfassen.kategorie": "Category",
  "erfassen.anlegen": "Add",
  "erfassen.naechster": "Next item",
  "erfassen.offline": "No connection – the item is saved and will be sent once you are back online.",
  "offline.warten": "waiting for connection",
  "offline.senden": "Send now",
  "profil.titel": "Profile",
  "profil.kontakt": "Contact",
  "profil.auszahlung": "Payout",
  "profil.speichern": "Save",
  "profil.sprache": "Language",
  "profil.anzeige": "Display",
  "profil.grosseSchrift": "Large text",
  "profil.einfach": "Simple mode (only the essentials on Home)",
  "profil.kacheln": "Customise Home",
  "profil.abmelden": "Sign out",
  "profil.sicherheit": "Security and access",
  "profil.werben": "Refer friends",
  "sicherheit.titel": "Security and access",
  "sicherheit.geraete": "Signed-in devices",
  "sicherheit.loeschen": "Delete account",
};

const UK: Record<Schluessel, string> = {
  zurueck: "Назад",
  "nav.start": "Головна",
  "nav.artikel": "Товари",
  "nav.erfassen": "Додати",
  "nav.abrechnung": "Розрахунки",
  "nav.regal": "Полиця",
  "nav.profil": "Профіль",
  "nav.portal": "Портал продавця",
  "start.guthaben": "Твій баланс",
  "start.heuteNichts": "Сьогодні ще нічого не продано",
  "start.heute": "Сьогодні",
  "start.losGehts": "Почнімо",
  "start.fuerDich": "Для тебе",
  "start.letzteVerkaeufe": "Останні продажі",
  "start.live": "Наживо",
  "start.nochNichts": "Поки нічого не продано. Щойно щось пройде через касу, воно з’явиться тут – одразу.",
  "start.zahlen": "Твої цифри · 30 днів",
  "start.umsatz": "Виторг",
  "start.verkauft": "Продано",
  "start.preis": "Сер. ціна",
  "start.imRegal": "Сер. днів на полиці",
  "start.regalCheck": "Перевірка полиці",
  "start.app": "Додати на телефон",
  "start.verkauftRuf": "Продано!",
  "kachel.erfassen": "Додати товар",
  "kachel.erfassenSub": "Спочатку фото, потім ціна",
  "kachel.etiketten": "Друк етикеток",
  "kachel.packliste": "Список для викладки",
  "kachel.regal": "Моя полиця",
  "kachel.lagerhueter": "Залежалось",
  "kachel.abrechnung": "Розрахунки",
  "kachel.teilen": "Поділитися",
  "kachel.statistik": "Твої цифри",
  "kachel.aktionen": "Акції",
  "kachel.inventur": "Інвентаризація",
  "kachel.termin": "Запис",
  "kachel.sammel": "Багато одразу",
  "erfassen.schritt1": "Зробити фото",
  "erfassen.schritt2": "Що це і скільки коштує?",
  "erfassen.schritt3": "Додано",
  "erfassen.foto": "Торкнись і сфотографуй",
  "erfassen.fotoHinweis": "При денному світлі, товар заповнює кадр",
  "erfassen.galerie": "З галереї",
  "erfassen.ohneFoto": "Далі без фото",
  "erfassen.bezeichnung": "Назва",
  "erfassen.preis": "Ціна",
  "erfassen.kategorie": "Категорія",
  "erfassen.anlegen": "Додати",
  "erfassen.naechster": "Наступний товар",
  "erfassen.offline": "Немає зв’язку – товар збережено і буде надіслано, щойно ти знову онлайн.",
  "offline.warten": "очікують на з’єднання",
  "offline.senden": "Надіслати зараз",
  "profil.titel": "Профіль",
  "profil.kontakt": "Контакт",
  "profil.auszahlung": "Виплата",
  "profil.speichern": "Зберегти",
  "profil.sprache": "Мова",
  "profil.anzeige": "Відображення",
  "profil.grosseSchrift": "Великий шрифт",
  "profil.einfach": "Простий режим (лише головне на старті)",
  "profil.kacheln": "Налаштувати головну",
  "profil.abmelden": "Вийти",
  "profil.sicherheit": "Безпека і доступ",
  "profil.werben": "Запросити друзів",
  "sicherheit.titel": "Безпека і доступ",
  "sicherheit.geraete": "Пристрої з входом",
  "sicherheit.loeschen": "Видалити обліковий запис",
};

const TR: Record<Schluessel, string> = {
  zurueck: "Geri",
  "nav.start": "Başlangıç",
  "nav.artikel": "Ürünler",
  "nav.erfassen": "Ekle",
  "nav.abrechnung": "Hesap",
  "nav.regal": "Raf",
  "nav.profil": "Profil",
  "nav.portal": "Satıcı portalı",
  "start.guthaben": "Bakiyen",
  "start.heuteNichts": "Bugün henüz satış yok",
  "start.heute": "Bugün",
  "start.losGehts": "Hadi başlayalım",
  "start.fuerDich": "Senin için",
  "start.letzteVerkaeufe": "Son satışlar",
  "start.live": "Canlı",
  "start.nochNichts": "Henüz satış yok. Kasadan bir şey geçer geçmez burada görünür – anında.",
  "start.zahlen": "Rakamların · 30 gün",
  "start.umsatz": "Ciro",
  "start.verkauft": "Satıldı",
  "start.preis": "Ort. fiyat",
  "start.imRegal": "Ort. rafta gün",
  "start.regalCheck": "Raf kontrolü",
  "start.app": "Telefona uygulama olarak ekle",
  "start.verkauftRuf": "Satıldı!",
  "kachel.erfassen": "Ürün ekle",
  "kachel.erfassenSub": "Önce fotoğraf, sonra fiyat",
  "kachel.etiketten": "Etiket yazdır",
  "kachel.packliste": "Yerleştirme listesi",
  "kachel.regal": "Rafım",
  "kachel.lagerhueter": "Uzun süredir duran",
  "kachel.abrechnung": "Hesap",
  "kachel.teilen": "Paylaş",
  "kachel.statistik": "Rakamların",
  "kachel.aktionen": "Kampanyalar",
  "kachel.inventur": "Sayım",
  "kachel.termin": "Randevu",
  "kachel.sammel": "Toplu ekleme",
  "erfassen.schritt1": "Fotoğraf çek",
  "erfassen.schritt2": "Bu ne, fiyatı ne?",
  "erfassen.schritt3": "Eklendi",
  "erfassen.foto": "Dokun ve fotoğraf çek",
  "erfassen.fotoHinweis": "Gün ışığında, ürün kareyi doldursun",
  "erfassen.galerie": "Galeriden",
  "erfassen.ohneFoto": "Fotoğrafsız devam et",
  "erfassen.bezeichnung": "Açıklama",
  "erfassen.preis": "Fiyat",
  "erfassen.kategorie": "Kategori",
  "erfassen.anlegen": "Ekle",
  "erfassen.naechster": "Sonraki ürün",
  "erfassen.offline": "Bağlantı yok – ürün kaydedildi, tekrar çevrimiçi olunca gönderilecek.",
  "offline.warten": "bağlantı bekliyor",
  "offline.senden": "Şimdi gönder",
  "profil.titel": "Profil",
  "profil.kontakt": "İletişim",
  "profil.auszahlung": "Ödeme",
  "profil.speichern": "Kaydet",
  "profil.sprache": "Dil",
  "profil.anzeige": "Görünüm",
  "profil.grosseSchrift": "Büyük yazı",
  "profil.einfach": "Basit mod (başlangıçta yalnızca en önemliler)",
  "profil.kacheln": "Başlangıcı düzenle",
  "profil.abmelden": "Çıkış yap",
  "profil.sicherheit": "Güvenlik ve erişim",
  "profil.werben": "Arkadaş davet et",
  "sicherheit.titel": "Güvenlik ve erişim",
  "sicherheit.geraete": "Oturum açık cihazlar",
  "sicherheit.loeschen": "Hesabı sil",
};

const WOERTERBUCH: Record<Sprache, Record<Schluessel, string>> = { de: DE, en: EN, uk: UK, tr: TR };
const SPEICHER = "rl-sprache";

export function spracheLesen(): Sprache {
  try {
    const s = localStorage.getItem(SPEICHER);
    if (s === "de" || s === "en" || s === "uk" || s === "tr") return s;
  } catch {
    // privater Modus
  }
  return "de";
}
export function spracheMerken(s: Sprache): void {
  try {
    localStorage.setItem(SPEICHER, s);
  } catch {
    // ignorieren
  }
  document.documentElement.lang = s;
}

export function uebersetze(sprache: Sprache, key: Schluessel): string {
  return WOERTERBUCH[sprache][key] ?? DE[key];
}

/** Übersetzungsfunktion für Komponenten im Mieterportal; folgt der Einstellung im Profil, vorher dem gemerkten Wert. */
export function useT(): (key: Schluessel) => string {
  const ck = useCockpit();
  const sprache = ck.data?.einstellungen.sprache ?? spracheLesen();
  return (key) => uebersetze(sprache, key);
}
export function useSprache(): Sprache {
  const ck = useCockpit();
  return ck.data?.einstellungen.sprache ?? spracheLesen();
}

/** Übersetzung nach deutschem Text (für alle übrigen Portalseiten, E12.1). */
export function useTx(): (deutsch: string) => string {
  const ck = useCockpit();
  const sprache = ck.data?.einstellungen.sprache ?? spracheLesen();
  return (deutsch) => uebersetzeText(sprache, deutsch);
}
